/**
 * QuotationStatusBadge
 *
 * Thin wrapper around the shared UI StatusBadge using the "quotation" type.
 * Keeps the same external API so callers do not need to change.
 *
 * Status values (from QuotationStatus enum):
 *   DRAFT | SENT | ACCEPTED | REJECTED | EXPIRED | CONVERTED | CANCELLED | SUPERSEDED
 *
 * CANCELLED / SUPERSEDED (มติ PO 29 ก.ย. 2026) ยังไม่มีใน `config/designSystem.ts` (ไฟล์กลางนอกขอบเขตโมดูลนี้)
 * — ถ้าปล่อยผ่าน StatusBadge จะ fallback เป็น generic.inactive (ป้าย "ไม่ใช้งาน" ผิดความหมาย) จึงกำหนดสี/ป้ายไว้ที่นี่
 * ด้วย markup เดียวกับ StatusBadge (pill · dot · border) · เมื่อย้ายเข้า designSystem แล้วให้ลบ map นี้
 */

import React from 'react';
import { QuotationStatus } from '../../types/quotation';
import { StatusBadge } from '../ui/StatusBadge';
import { cn } from '../../utils/cn';

interface QuotationStatusBadgeProps {
  status: QuotationStatus;
}

/** สถานะที่ designSystem ยังไม่รู้จัก — สี/ป้ายไทยตาม contract: CANCELLED เทาแดง · SUPERSEDED เทา */
export const QUOTATION_EXTRA_STATUS_STYLES: Record<
  'CANCELLED' | 'SUPERSEDED',
  { label: string; bg: string; text: string; border: string; dot: string }
> = {
  CANCELLED: {
    label: 'ยกเลิก',
    bg: 'bg-secondary-100',
    text: 'text-danger-700',
    border: 'border-danger-200',
    dot: 'bg-danger-500',
  },
  SUPERSEDED: {
    label: 'แทนที่แล้ว',
    bg: 'bg-secondary-100',
    text: 'text-secondary-600',
    border: 'border-secondary-300',
    dot: 'bg-secondary-400',
  },
};

const QuotationStatusBadge: React.FC<QuotationStatusBadgeProps> = ({ status }) => {
  const extra = QUOTATION_EXTRA_STATUS_STYLES[status as keyof typeof QUOTATION_EXTRA_STATUS_STYLES];
  if (extra) {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1.5 font-medium whitespace-nowrap px-2 py-0.5 text-xs rounded-full border transition-colors duration-150',
          extra.bg,
          extra.text,
          extra.border
        )}
      >
        <span className={cn('rounded-full flex-shrink-0 w-1.5 h-1.5', extra.dot)} />
        {extra.label}
      </span>
    );
  }
  return (
    <StatusBadge
      type="quotation"
      status={status as Parameters<typeof StatusBadge<'quotation'>>[0]['status']}
      showDot
      bordered
    />
  );
};

export default QuotationStatusBadge;
